import { AsyncPipe, NgClass } from '@angular/common';
import {
  AfterContentChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  DynamicFormModel,
  DynamicFormService,
  DynamicInputModel,
  DynamicRadioGroupModel,
  DynamicSelectModel,
} from '@ng-dynamic-forms/core';
import {
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  LanguageService,
  RoutingService,
  TranslatePipe,
  TranslationService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import { Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BaseSiteConfigService } from 'src/app/service/base-site/base-site-config.service';
import { ComponentService } from 'src/app/service/component.service';
import {
  ManagementEmployeeService,
  PostAddEmployeesPayload,
} from 'src/app/service/myaccount/management-employee.service';
import {
  GetB2BUnitAddPayload,
  ShimanoDynamicFormService,
} from 'src/app/service/shimano-dynamic-form/shimano-dynamic-form.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { DynamicFormsComponent } from '../../dynamic-forms/dynamic-forms.component';
import { MyaccountEmployeeAddressComponent } from '../myaccount-employee-address/myaccount-employee-address.component';
import { MyaccountPermissionsComponent } from '../myaccount-permissions/myaccount-permissions.component';

@Component({
  selector: 'app-add-employee',
  templateUrl: './add-employee.component.html',
  styleUrls: ['./add-employee.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [AsyncPipe],
  imports: [
    DynamicFormsComponent,
    NgClass,
    MyaccountEmployeeAddressComponent,
    MyaccountPermissionsComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class AddEmployeeComponent implements OnInit, OnDestroy, AfterContentChecked {
  formGroup: FormGroup;
  addEmployeeFormModel: DynamicFormModel;
  userAddresses$: Observable<any>;
  errorMessage: any;
  count = 0;
  isMobile = false;
  isValid = false;
  cxMobileWidth = this.breakpointService.getSize(BREAKPOINT.sm);
  currentPageSubscription$: Subscription;
  addEmployeeModelSubscription$: Subscription;
  addEmployeeFormGroupSubscription$: Subscription;
  b2bUnitAddressSubscription$: Subscription;
  myAccountPermissionSubscription$: Subscription;
  accountPermissionsEnabled: boolean;
  buttonNoUpdate = false;
  accountPermissions: any;
  accountAddresses: any;
  defaultAddress: any;
  formCheckValueChangesSubs: Subscription;
  statusErrorMsgSubscription$: Subscription;
  setAccountPermission: any = ['PLACE_ORDERS', 'VIEW_WHOLESALE_PRICE', 'VIEW_ORDER_HISTORY'];
  isAddressSelected: boolean = false;

  b2bUnitAddressesPayload: GetB2BUnitAddPayload = {
    fields: 'DEFAULT',
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private componentService: ComponentService,
    private cmsService: CmsService,
    private formService: DynamicFormService,
    private breakpointService: BreakpointService,
    private globalMessageService: GlobalMessageService,
    private manageEmployeeService: ManagementEmployeeService,
    private dynamicFormService: ShimanoDynamicFormService,
    private routingService: RoutingService,
    private messageService: GlobalMessageService,
    protected translation: TranslationService,
    private languageService: LanguageService,
    private pipeAsync: AsyncPipe,
    private baseSiteConfigService: BaseSiteConfigService,
    private storageService: StorageService
  ) {}

  ngOnInit() {
    this.isMobile = window.innerWidth < this.cxMobileWidth;
    this.storageService.removeSessionStorageItem('status');
    this.storageService.removeSessionStorageItem('lastname');
    this.storageService.removeSessionStorageItem('firstname');
    this.storageService.removeSessionStorageItem('email');
    this.storageService.removeSessionStorageItem('jobtitle');
    this.storageService.removeSessionStorageItem('address');
    this.storageService.removeSessionStorageItem('formattedadd');
    this.storageService.removeSessionStorageItem('useruid');
    this.storageService.removeSessionStorageItem('storeFrontPermissions');
    this.storageService.removeSessionStorageItem('shipToAddresses');

    this.currentPageSubscription$ = this.cmsService.getCurrentPage().subscribe((currentPage) => {
      if (currentPage && currentPage.pageId.includes('accountAddEmployeePage')) {
        this.addEmployeeModelSubscription$ = this.componentService.dynamicFormModel$.subscribe(
          (addEmployeeFormModel) => {
            this.updateFormModel(addEmployeeFormModel);
          }
        );

        this.addEmployeeFormGroupSubscription$ = this.componentService.dynamicFormGroup$.subscribe(
          (addEmployeeFormGroup) => {
            this.updateFormGroup(addEmployeeFormGroup);
          }
        );
      }
    });

    this.isMyAccountPermissionsEnabled();
    this.formService.detectChanges();
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  updateFormModel(formModel: any) {
    this.addEmployeeFormModel = formModel;
    this.b2bUnitAddressSubscription$ = this.dynamicFormService
      .getB2BUnitAddresses(this.b2bUnitAddressesPayload)
      .subscribe((data) => {
        if (data.items && data.items.length) {
          const optionCountriesValues: any = [];
          for (const key in data.items) {
            if (key !== '' || key !== null) {
              const options = {
                label: data.items[key].name,
                value: data.items[key].uid,
              };
              optionCountriesValues.push(options);
            }
          }
          const inputAddressesModel = this.formService.findModelById<DynamicSelectModel<string>>(
            'deliveryAddress',
            formModel
          );
          if (inputAddressesModel !== null) {
            inputAddressesModel.options = optionCountriesValues;
            this.formService.detectChanges();
          }
        }
      });

    const radioStatusModel = this.formService.findModelById<DynamicRadioGroupModel<string>>(
      'userStatus',
      this.addEmployeeFormModel
    );
    if (radioStatusModel !== null) {
      this.statusErrorMsgSubscription$ = this.getTranslatedError('userStatus').subscribe((translatedErrorMessage) => {
        radioStatusModel.errorMessages.required = translatedErrorMessage;
      });
    }

    const inputLastNameModel = this.formService.findModelById<DynamicInputModel>(
      'lastName_medium',
      this.addEmployeeFormModel
    );
    if (inputLastNameModel !== null) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(`lastName_medium`)),
      };
      inputLastNameModel.errorMessages = hasError;
    }
    const inputFirstNameModel = this.formService.findModelById<DynamicInputModel>(
      'firstName_medium',
      this.addEmployeeFormModel
    );
    if (inputFirstNameModel !== null) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(`firstName_medium`)),
      };
      inputFirstNameModel.errorMessages = hasError;
    }
    const inputEmailModel = this.formService.findModelById<DynamicInputModel>(
      'emailAddress_medium',
      this.addEmployeeFormModel
    );
    if (inputEmailModel !== null) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(`emailAddress_full`)),
        pattern: this.pipeAsync.transform(this.getTranslatedError(`emailAddress_medium`)),
      };
      inputEmailModel.errorMessages = hasError;
    }
    const inputJobTitleModel = this.formService.findModelById<DynamicInputModel>('jobTitle', this.addEmployeeFormModel);
    if (inputJobTitleModel !== null) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(`jobTitle`)),
      };
      inputJobTitleModel.errorMessages = hasError;
    }
    const selectAddressModel = this.formService.findModelById<DynamicSelectModel<string>>(
      'deliveryAddress',
      this.addEmployeeFormModel
    );
    if (selectAddressModel !== null) {
      const hasError = {
        required: this.pipeAsync.transform(this.getTranslatedError(`deliveryAddress`)),
      };
      selectAddressModel.errorMessages = hasError;
    }
    this.formService.detectChanges();
  }

  updateFormGroup(formGroup: any): void {
    this.formGroup = formGroup;

    /* FOR SEU AND OTHER SSO ONLY */
    if (!this.accountPermissionsEnabled) {
      if (this.formGroup.value) {
        this.isAddressSelected = true;
      }
    }
  }

  onSubmit($event: any, request: string) {
    if (typeof this.formGroup !== 'undefined') {
      if (this.formGroup.status.toLowerCase() === 'valid') {
        this.fullPageOverlaySpinner('show');

        /* SNAH ONLY */
        if (this.accountPermissionsEnabled) {
          this.accountPermissions =
            this.accountPermissions !== undefined ? this.accountPermissions : this.setAccountPermission;
        }

        this.defaultAddress = this.formGroup.get('deliveryAddress')
          ? this.formGroup.value.deliveryAddress
          : this.accountAddresses[0];

        const addEmployeePayload: PostAddEmployeesPayload = {
          active: this.formGroup.value.userStatus === 'true' ? true : false,
          firstName: this.formGroup.value.firstName_medium,
          lastName: this.formGroup.value.lastName_medium,
          jobTitle: this.formGroup.value.jobTitle,
          id: this.defaultAddress,
          email: this.formGroup.value.emailAddress_medium,
          customerId: this.formGroup.value.emailAddress_medium,
          shipToAddrsId: this.accountAddresses,
          storeFrontPermissions: this.accountPermissions,
        };

        this.manageEmployeeService
          .postAddEmployee(addEmployeePayload)
          .pipe(take(1))
          .subscribe(
            (data) => {
              if (data === null) {
                if (request === 'close') {
                  this.routingService.goByUrl('/my-account');
                  this.messageService.add(
                    {
                      key: 'myAccount.successAddEmployee',
                    },
                    GlobalMessageType.MSG_TYPE_CONFIRMATION
                  );
                } else {
                  this.messageService.add(
                    {
                      key: 'myAccount.successAddEmployee',
                    },
                    GlobalMessageType.MSG_TYPE_CONFIRMATION
                  );
                  this.formGroup.reset();
                  this.componentService.resetAddEmployeeForm$.next(true);
                }
              }
              this.fullPageOverlaySpinner('hide');
            },
            (err) => {
              this.globalMessageService.remove(GlobalMessageType.MSG_TYPE_ERROR);
              this.globalMessageService.add(err.error.errors[0].message, GlobalMessageType.MSG_TYPE_ERROR);
              this.fullPageOverlaySpinner('hide');
            }
          );
      }
    }
  }

  get activeLanguage$(): Observable<string> {
    return this.languageService.getActive();
  }

  getTranslatedError(id: string): Observable<string> {
    return this.translation.translate(`error.dynamicform.${id}`);
  }

  isMyAccountPermissionsEnabled = () => {
    this.myAccountPermissionSubscription$ = this.getMyAccountPermissionSiteConfig$().subscribe((permisssion) => {
      this.accountPermissionsEnabled = permisssion;
      this.cdr.detectChanges();
    });
  };

  getMyAccountPermissionSiteConfig$(): Observable<boolean> {
    const key = 'shimano.config.storefront.permission.enabled';
    return this.baseSiteConfigService
      .getBaseSiteConfigValues$([key])
      .pipe(
        map((configs) => configs && configs.filter((config: any) => config.key === key).map((res: any) => res.value)[0])
      );
  }
  hasAddressSelected(value: boolean) {
    this.isAddressSelected = value ? true : false;
  }

  activePermission(permissions: any) {
    this.accountPermissions = permissions;
  }

  activeAddresses(addresses: any) {
    this.accountAddresses = addresses;
  }

  hasChanged(hasChanged: any) {
    if (hasChanged !== undefined) {
      this.disablingButton(hasChanged);
    }
  }

  disablingButton(value: boolean) {
    if (value) {
      this.buttonNoUpdate = false;
    } else {
      this.buttonNoUpdate = true;
    }
  }

  fullPageOverlaySpinner(value: string): void {
    if (value === 'show') {
      document.querySelector('cx-storefront').classList.add('fp-overlay-spinner');
    } else {
      document.querySelector('cx-storefront').classList.remove('fp-overlay-spinner');
    }
  }

  ngOnDestroy() {
    if (this.currentPageSubscription$) {
      this.currentPageSubscription$.unsubscribe();
    }
    if (this.addEmployeeModelSubscription$) {
      this.addEmployeeModelSubscription$.unsubscribe();
    }
    if (this.addEmployeeFormGroupSubscription$) {
      this.addEmployeeFormGroupSubscription$.unsubscribe();
    }
    if (this.b2bUnitAddressSubscription$) {
      this.b2bUnitAddressSubscription$.unsubscribe();
    }
    if (this.myAccountPermissionSubscription$) {
      this.myAccountPermissionSubscription$.unsubscribe();
    }
    this.formCheckValueChangesSubs?.unsubscribe();
    this.statusErrorMsgSubscription$?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    setTimeout(() => {
      this.isMobile = window.innerWidth < this.cxMobileWidth;
      this.cdr.detectChanges();
    }, 500);
  }
}
